// ---------------------------------------------------------------------------
// useMultiMonthUpload — estado e logica do modal "Enviar multiplos meses".
// ---------------------------------------------------------------------------
// Fluxo:
//   1) Step 1 (``files``)
//      - usuario seleciona arquivos (file picker ou folder picker), com
//        validacao por extensao (mesmo padrao do drawer / V2).
//      - usuario seleciona um conjunto de meses-alvo na grade.
//      - botao "Prosseguir" → dispara o match e avanca para step 2 ja
//        em estado de loading.
//
//   2) Step 2 (``match`` + ``match_loading``)
//      - chama ``POST /api/chat/file-month-match/`` com filenames +
//        labels dos meses + nome do input. Backend retorna pareamento
//        com nivel de confianca (high/medium/none).
//      - se a chamada falha, cai num fallback que devolve todos com
//        ``month: null`` e exibe um aviso discreto. Usuario pode
//        ajustar manualmente cada linha.
//      - botao "Iniciar upload" bloqueia se 100% das linhas estiverem
//        sem mes (caso contrario, prosseguir e ignorar as sem mes).
//
//   3) Step 3 (``upload`` + ``upload_done``)
//      - itera as linhas com mes definido em ordem (sequencial) e
//        chama ``uploadInputV3`` para cada (year, month, file).
//      - cada item vira uma row na lista com status individual:
//        waiting → uploading → success | error | interrupted.
//      - "Interromper" mata a iteracao no proximo iteration tick e
//        marca os pendentes como ``interrupted``. As linhas ja em
//        sucesso ou erro permanecem como estao.
//      - "Tentar novamente" reabre o passo so com as linhas em erro.
//
// O hook nao toca DOM nem JSX — devolve o estado puro pra que o modal
// e os subcomponentes formatem.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import {
  fetchInputDeliveredMonthsV3,
  matchFilenamesToMonthsV3,
  uploadInputBroadcastV3,
  uploadInputV3,
} from "../../../../services/inputsV3Service";
import { isExtensionValid } from "../../inputs_components/utils/fileTypes";
import { monthKey, formatMonthShort } from "./MonthRangeGrid";

const MONTH_ABBR_PT = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

// Label do mes no MESMO formato que o backend AIFileMonthMatchView
// espera ("MMM-YY" em PT). Mantemos PT-only intencionalmente — o
// prompt do match e' lingua-agnostico, e usar PT permite reproducir
// a UX exata do SpecialOptionsModal V2.
function monthLabelForMatch(year, month) {
  return `${MONTH_ABBR_PT[month - 1]}-${String(year).slice(-2)}`;
}

// Limite de seguranca — o usuario pode escolher uma pasta com
// centenas de arquivos por engano. 24 cobre 2 anos de uploads
// mensais, o cenario realista mais agressivo.
const MAX_FILES = 24;

const STEPS = {
  FILES: "files", // step 1: pickers + grid
  MATCH_LOADING: "match_loading", // step 2 loading (so no modo multi)
  MATCH: "match", // step 2 (revisar pareamento, so no modo multi)
  UPLOAD: "upload", // step UPLOAD (em curso)
  UPLOAD_DONE: "upload_done", // step UPLOAD (finalizado)
};

// Modos do modal (rev. 2026-05-26).
//
// ``multi``  - default; varios arquivos -> varios meses, fluxo 3 steps
//              (Arquivos+Periodo, Confirmacao, Upload).
// ``single`` - 1 arquivo aplicado em N meses, fluxo 2 steps
//              (Arquivo+Periodo, Upload). Sem etapa de pareamento.
//              Backend faz o fan-out via ``upload-multi/``.
const MODES = {
  MULTI: "multi",
  SINGLE: "single",
};

export default function useMultiMonthUpload({
  open,
  appName,
  metaName,
  inputDisplayName,
  expectedFileTypes,
  availableMonths,
  anchorYear,
  anchorMonth,
  onUploaded,
}) {
  // ── Step ────────────────────────────────────────────────────────────
  const [step, setStep] = useState(STEPS.FILES);

  // ── Modo do modal (multi-arquivos vs unico-arquivo) ─────────────────
  const [mode, setModeState] = useState(MODES.MULTI);

  // ── Step 1: arquivos + meses ────────────────────────────────────────
  const [files, setFiles] = useState([]); // File[]
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());

  // ── Modo SINGLE: progresso/status do upload broadcast ──────────────
  // Como existe uma unica chamada HTTP, modelamos com um state
  // singular em vez do array ``uploads``. ``status`` espelha as
  // labels da i18n: ``idle | uploading | success | error``.
  const [singleStatus, setSingleStatus] = useState("idle");
  const [singleProgress, setSingleProgress] = useState(0);
  const [singleError, setSingleError] = useState(null);
  const [singleResults, setSingleResults] = useState([]);

  // ── Meses ja entregues (vindos do backend, p/ a bolinha laranja) ──
  const [deliveredKeys, setDeliveredKeys] = useState(() => new Set());

  // ── Step 2: pareamento ──────────────────────────────────────────────
  // shape: [{ file: File, month: "YYYY-M" | null, confidence: "high|medium|manual|none" }]
  const [pairs, setPairs] = useState([]);

  // ── Step 3: upload ──────────────────────────────────────────────────
  // shape: [{ file, target: "YYYY-M", status, progress, error? }]
  const [uploads, setUploads] = useState([]);
  // Flag interrupcao: lida pelo loop sequencial entre iteracoes para
  // abortar pendentes sem matar o que ja terminou.
  const interruptedRef = useRef(false);

  // ── Reset quando o modal abre/fecha ─────────────────────────────────
  useEffect(() => {
    if (!open) return;
    // Ao abrir, partimos do step 1 mas preservamos arquivos/meses se
    // o usuario voltou e abriu de novo na mesma sessao. ResetAll fica
    // disponivel pra quando o consumidor quiser limpar tudo.
    setStep(STEPS.FILES);
    interruptedRef.current = false;
    setSingleStatus("idle");
    setSingleProgress(0);
    setSingleError(null);
    setSingleResults([]);
  }, [open]);

  // ── Troca de modo: descarta arquivos selecionados ──────────────────
  // Decisao UX (rev. 2026-05-26): troca de modo zera arquivos para
  // evitar arrastar uma selecao multi-arquivo para o modo single (que
  // so aceita 1) ou vice-versa. Periodo selecionado e' preservado —
  // a intencao de "quais meses receber" e' a mesma em ambos os modos
  // e o consultor frequentemente quer manter esse contexto ao
  // alternar para comparar.
  const setMode = useCallback(
    (nextMode) => {
      if (nextMode !== MODES.MULTI && nextMode !== MODES.SINGLE) return;
      if (nextMode === mode) return;
      setModeState(nextMode);
      setFiles([]);
      setPairs([]);
      setUploads([]);
      setSingleStatus("idle");
      setSingleProgress(0);
      setSingleError(null);
      setSingleResults([]);
      setStep(STEPS.FILES);
    },
    [mode],
  );

  // Carrega meses ja entregues quando o modal abre (uma vez por
  // (app, meta)). Falha silenciosa — a UI degrada para "sem
  // marcacao laranja".
  useEffect(() => {
    if (!open || !appName || !metaName) return undefined;
    let cancelled = false;
    fetchInputDeliveredMonthsV3({ appName, metaName })
      .then((data) => {
        if (cancelled) return;
        const ks = new Set();
        (data?.delivered || []).forEach((d) => {
          ks.add(monthKey(d.year, d.month));
        });
        setDeliveredKeys(ks);
      })
      .catch(() => {
        if (!cancelled) setDeliveredKeys(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [open, appName, metaName]);

  // ── Arquivos: add/remove/clear ──────────────────────────────────────
  // No modo ``single`` o limite efetivo e' 1 (substituicao em vez de
  // append): se o consultor escolhe um novo arquivo, ele troca o
  // anterior. Mantem semantica intuitiva — clicar "Selecionar arquivo"
  // duas vezes nao acumula uma lista.
  const addFiles = useCallback(
    (fileList) => {
      const incoming = Array.from(fileList || []);
      if (incoming.length === 0) return { added: 0, rejected: 0, truncated: 0 };
      // Filtra por extensao (mesmo padrao do drawer/V2).
      const filtered = incoming.filter((f) => {
        const ext = (f.name.split(".").pop() || "").toLowerCase();
        return isExtensionValid(ext, expectedFileTypes);
      });
      const rejected = incoming.length - filtered.length;
      if (mode === MODES.SINGLE) {
        // SINGLE: substitui — pega apenas o primeiro do que veio
        // valido. Se nada validou, nao mexe na selecao atual (mantem o
        // arquivo previo se existia).
        if (filtered.length === 0) {
          return { added: 0, rejected, truncated: 0 };
        }
        const next = filtered[0];
        const truncated = filtered.length - 1;
        setFiles([next]);
        return { added: 1, rejected, truncated };
      }
      // MULTI: append + dedupe + limite agregado de MAX_FILES.
      const existingNames = new Set(files.map((f) => f.name));
      const novel = filtered.filter((f) => !existingNames.has(f.name));
      const remaining = Math.max(0, MAX_FILES - files.length);
      const accepted = novel.slice(0, remaining);
      const truncated = novel.length - accepted.length;
      if (accepted.length > 0) {
        setFiles((prev) => [...prev, ...accepted]);
      }
      return { added: accepted.length, rejected, truncated };
    },
    [expectedFileTypes, files, mode],
  );

  const removeFile = useCallback((idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const clearFiles = useCallback(() => setFiles([]), []);

  // ── Step 1 -> Step 2: dispara o match ───────────────────────────────
  const startMatch = useCallback(async () => {
    if (files.length === 0 || selectedKeys.size === 0) return;
    setStep(STEPS.MATCH_LOADING);

    const filenames = files.map((f) => f.name);
    const monthsInRange = [...selectedKeys]
      .map((k) => {
        const [y, m] = k.split("-").map(Number);
        return { year: y, month: m, key: k, label: monthLabelForMatch(y, m) };
      })
      .sort((a, b) => a.year * 12 + a.month - (b.year * 12 + b.month));
    const monthLabels = monthsInRange.map((mm) => mm.label);
    const labelToKey = new Map(monthsInRange.map((mm) => [mm.label, mm.key]));

    try {
      const data = await matchFilenamesToMonthsV3({
        filenames,
        months: monthLabels,
        inputName: inputDisplayName || metaName || "",
      });
      const matches = data?.matches || [];
      const next = files.map((file) => {
        const m = matches.find((x) => x.filename === file.name);
        const key = m?.month ? labelToKey.get(m.month) || null : null;
        return {
          file,
          month: key,
          confidence: key ? m?.confidence || "manual" : "none",
        };
      });
      setPairs(next);
    } catch (_err) {
      // Fallback silencioso: tudo sem mes, usuario ajusta manualmente.
      setPairs(
        files.map((file) => ({ file, month: null, confidence: "none" })),
      );
      toast(
        "Não foi possível sugerir o pareamento automaticamente. Selecione o mês de cada arquivo manualmente.",
        { position: "top-right", type: "info", autoClose: 4000 },
      );
    } finally {
      setStep(STEPS.MATCH);
    }
  }, [files, selectedKeys, inputDisplayName, metaName]);

  // ── Step 2: edicao manual de uma linha ──────────────────────────────
  const setPairMonth = useCallback((idx, monthOrNull) => {
    setPairs((prev) => {
      const next = [...prev];
      next[idx] = {
        ...next[idx],
        month: monthOrNull || null,
        confidence: monthOrNull ? "manual" : "none",
      };
      return next;
    });
  }, []);

  // ── Step 2 -> Step 3: inicia upload sequencial ──────────────────────
  const startUpload = useCallback(async () => {
    const valid = pairs.filter((p) => p.month);
    if (valid.length === 0) return;
    interruptedRef.current = false;
    const initial = valid.map((p) => {
      const [y, m] = p.month.split("-").map(Number);
      return {
        file: p.file,
        targetYear: y,
        targetMonth: m,
        target: formatMonthShort(p.month),
        status: "waiting",
        progress: 0,
        error: null,
      };
    });
    setUploads(initial);
    setStep(STEPS.UPLOAD);

    // Loop sequencial: usamos updater funcional pra evitar stale state.
    for (let i = 0; i < initial.length; i++) {
      if (interruptedRef.current) {
        // marca o atual + restantes como interrupted
        setUploads((prev) =>
          prev.map((u, idx) =>
            idx >= i ? { ...u, status: "interrupted", progress: 0 } : u,
          ),
        );
        break;
      }
      const row = initial[i];
      // Coloca em "uploading" — progresso comeca em 0 e e' atualizado
      // pelo callback ``onProgress`` do axios (config per-request).
      setUploads((prev) =>
        prev.map((u, idx) =>
          idx === i ? { ...u, status: "uploading", progress: 0 } : u,
        ),
      );
      try {
        await uploadInputV3({
          appName,
          year: row.targetYear,
          month: row.targetMonth,
          metaName,
          file: row.file,
          onProgress: (pct) => {
            setUploads((prev) =>
              prev.map((u, idx) =>
                idx === i && u.status === "uploading"
                  ? { ...u, progress: pct }
                  : u,
              ),
            );
          },
        });
        setUploads((prev) =>
          prev.map((u, idx) =>
            idx === i ? { ...u, status: "success", progress: 100 } : u,
          ),
        );
      } catch (err) {
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.error_code ||
          err?.message ||
          "Falha no envio";
        setUploads((prev) =>
          prev.map((u, idx) =>
            idx === i
              ? { ...u, status: "error", progress: 100, error: String(msg) }
              : u,
          ),
        );
      }
    }
    setStep(STEPS.UPLOAD_DONE);
    if (typeof onUploaded === "function") {
      try {
        onUploaded();
      } catch {
        /* noop */
      }
    }
  }, [pairs, appName, metaName, onUploaded]);

  // ── Modo SINGLE: dispara o broadcast (1 arquivo → N meses) ─────────
  // Pula o step de pareamento — vai direto para o step UPLOAD com uma
  // unica chamada HTTP. Backend (``upload-multi/``) faz o fan-out.
  const startBroadcastUpload = useCallback(async () => {
    if (mode !== MODES.SINGLE) return;
    if (files.length === 0 || selectedKeys.size === 0) return;
    const file = files[0];
    const periods = [...selectedKeys]
      .map((k) => {
        const [y, m] = k.split("-").map(Number);
        return { year: y, month: m };
      })
      .sort((a, b) => a.year * 12 + a.month - (b.year * 12 + b.month));
    setSingleStatus("uploading");
    setSingleProgress(0);
    setSingleError(null);
    setSingleResults([]);
    setStep(STEPS.UPLOAD);
    try {
      const data = await uploadInputBroadcastV3({
        appName,
        metaName,
        file,
        periods,
        onProgress: (pct) => setSingleProgress(pct),
      });
      setSingleProgress(100);
      setSingleResults(data?.results || []);
      setSingleStatus(data?.success ? "success" : "error");
      if (!data?.success) {
        setSingleError(data?.error_code || "broadcast_failed");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.error_code ||
        err?.message ||
        "Falha no envio";
      setSingleProgress(100);
      setSingleStatus("error");
      setSingleError(String(msg));
    } finally {
      setStep(STEPS.UPLOAD_DONE);
      if (typeof onUploaded === "function") {
        try {
          onUploaded();
        } catch {
          /* noop */
        }
      }
    }
  }, [mode, files, selectedKeys, appName, metaName, onUploaded]);

  // ── Step 3: interromper ─────────────────────────────────────────────
  const interruptUpload = useCallback(() => {
    interruptedRef.current = true;
  }, []);

  // ── Step 3: re-tentar os falhados ───────────────────────────────────
  const retryFailed = useCallback(async () => {
    const failedIdx = uploads
      .map((u, idx) => (u.status === "error" ? idx : -1))
      .filter((idx) => idx >= 0);
    if (failedIdx.length === 0) return;
    interruptedRef.current = false;
    // Reset dos falhados para waiting.
    setUploads((prev) =>
      prev.map((u, idx) =>
        failedIdx.includes(idx)
          ? { ...u, status: "waiting", progress: 0, error: null }
          : u,
      ),
    );
    setStep(STEPS.UPLOAD);
    // Processa so os falhados, na ordem original.
    for (const i of failedIdx) {
      if (interruptedRef.current) {
        setUploads((prev) =>
          prev.map((u, idx) =>
            failedIdx.includes(idx) && idx >= i && u.status === "waiting"
              ? { ...u, status: "interrupted", progress: 0 }
              : u,
          ),
        );
        break;
      }
      setUploads((prev) => {
        const u = prev[i];
        return prev.map((x, idx) =>
          idx === i ? { ...u, status: "uploading", progress: 0 } : x,
        );
      });
      const u = uploads[i];
      try {
        await uploadInputV3({
          appName,
          year: u.targetYear,
          month: u.targetMonth,
          metaName,
          file: u.file,
          onProgress: (pct) => {
            setUploads((prev) =>
              prev.map((x, idx) =>
                idx === i && x.status === "uploading"
                  ? { ...x, progress: pct }
                  : x,
              ),
            );
          },
        });
        setUploads((prev) =>
          prev.map((x, idx) =>
            idx === i ? { ...x, status: "success", progress: 100 } : x,
          ),
        );
      } catch (err) {
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.error_code ||
          err?.message ||
          "Falha no envio";
        setUploads((prev) =>
          prev.map((x, idx) =>
            idx === i
              ? { ...x, status: "error", progress: 100, error: String(msg) }
              : x,
          ),
        );
      }
    }
    setStep(STEPS.UPLOAD_DONE);
    if (typeof onUploaded === "function") {
      try {
        onUploaded();
      } catch {
        /* noop */
      }
    }
  }, [uploads, appName, metaName, onUploaded]);

  // ── Reset completo (consumo opcional pelo modal) ───────────────────
  const resetAll = useCallback(() => {
    setStep(STEPS.FILES);
    setFiles([]);
    setSelectedKeys(new Set());
    setPairs([]);
    setUploads([]);
    setSingleStatus("idle");
    setSingleProgress(0);
    setSingleError(null);
    setSingleResults([]);
    interruptedRef.current = false;
  }, []);

  // ── Navegacao auxiliar ──────────────────────────────────────────────
  const goBack = useCallback(() => {
    if (step === STEPS.MATCH || step === STEPS.MATCH_LOADING) {
      setStep(STEPS.FILES);
    }
  }, [step]);

  // ── Derivados / contadores ──────────────────────────────────────────
  const pairCounts = useMemo(() => {
    let matched = 0;
    pairs.forEach((p) => {
      if (p.month) matched += 1;
    });
    return { matched, total: pairs.length, unmatched: pairs.length - matched };
  }, [pairs]);

  const uploadCounts = useMemo(() => {
    let success = 0;
    let error = 0;
    let waiting = 0;
    uploads.forEach((u) => {
      if (u.status === "success") success += 1;
      else if (u.status === "error") error += 1;
      else if (u.status === "waiting" || u.status === "interrupted") {
        waiting += 1;
      }
    });
    return { success, error, waiting, total: uploads.length };
  }, [uploads]);

  // Step 3 ativo (em execucao) — usado pra bloquear close/click-away.
  // No modo single, ``isUploading`` continua valendo para o broadcast
  // (mesma logica de bloqueio do close).
  const isUploading = step === STEPS.UPLOAD;
  const isMatching = step === STEPS.MATCH_LOADING;

  // Validacoes para o botao do footer.
  const canProceedFromFiles =
    step === STEPS.FILES && files.length > 0 && selectedKeys.size > 0;
  const canStartUpload = step === STEPS.MATCH && pairCounts.matched > 0;

  return {
    // estado
    step,
    mode,
    setMode,
    files,
    selectedKeys,
    setSelectedKeys,
    deliveredKeys,
    pairs,
    uploads,
    // contadores
    pairCounts,
    uploadCounts,
    // estado do modo SINGLE
    singleStatus,
    singleProgress,
    singleError,
    singleResults,
    // flags
    isUploading,
    isMatching,
    canProceedFromFiles,
    canStartUpload,
    // anchor / contexto
    anchorYear,
    anchorMonth,
    availableMonths,
    maxFiles: MAX_FILES,
    // acoes
    addFiles,
    removeFile,
    clearFiles,
    startMatch,
    setPairMonth,
    startUpload,
    startBroadcastUpload,
    interruptUpload,
    retryFailed,
    resetAll,
    goBack,
  };
}

export { STEPS, MODES, MAX_FILES, monthLabelForMatch };
